import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaEye, FaTrash } from "react-icons/fa";
import { FaAngleLeft, FaAngleRight, FaPencil } from "react-icons/fa6";
import axios from "axios";
import { toast } from "react-toastify";
import BaseUrl from "../../base-api";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import Loader from "../Loader/Loader";
import AxiosInstance from "../../AxiosInstance";
import { handleAxiosError } from "../Utility/handleAxiosError";

const Tender = () => {

    const [tenders, setTenders] = useState([]);

    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();


    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    const backendPage = currentPage - 1;

    // Memoize fetchTenders to prevent unnecessary re-renders and ensure stable function reference
    const fetchTenders = useCallback(async (page = currentPage, query = searchQuery) => {
        const token = sessionStorage.getItem("userToken");
        if (!token) {
            // Redirect to login if no token is found
            navigate("/login");
            return;
        }
        setLoading(true); // Show loader while fetching
        try {
            const res = await AxiosInstance.get(`${BaseUrl}/api/notice/getAll`, { // Adjusted API endpoint for tenders
                params: {
                    page: backendPage,
                    size: itemsPerPage,


                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
            })

            if (res.data.status === 200) {

                setTenders(
                    res.data.data.content.map((item) => ({
                        id: item.key,
                        title: item.title,
                        file: item.fileUrl && item.fileUrl.length > 0 ? item.fileUrl[0] : "", // Assuming fileUrl is an array
                    }))
                );

                setTotalItems(res.data.data.totalItems || 0);

                setCurrentPage(page);
            } else {
                toast.error(res.data.message || "Failed to fetch tenders.");
            }
        } catch (error) {

            toast.error(error.response?.data?.message || "An error occurred while fetching tenders.");
        } finally {
            setLoading(false);
        }
    }, [backendPage, itemsPerPage, navigate, searchQuery]);


    useEffect(() => {
        fetchTenders(currentPage, searchQuery);
    }, [fetchTenders, currentPage, searchQuery]);


    const handlePageChange = (pageNumber) => {
        if (pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
        }
    };


    const handleNextPage = () => {
        if (currentPage * itemsPerPage < totalItems) {
            handlePageChange(currentPage + 1);
        }
    };


    const handlePreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };


    const [newTender, setNewTender] = useState({
        title: "",
        file: null,
    });

    const fileInputRef = useRef(null);

    const [currentTender, setCurrentTender] = useState(null);

    const [editTitle, setEditTitle] = useState("");

    const [currentEditId, setCurrentEditId] = useState("");

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleView = async (id) => {
  const tender = tenders.find((item) => item.id === id);
  const token = sessionStorage.getItem("userToken");

  try {
    const response = await fetch(`${BaseUrl}/api/notice/document/${id}`, {
      method: "GET",
      headers: {
        Authorization: token,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        toast.error("Document not found.");
      } else {
        toast.error("Failed to load the document.");
      }
      return; // Exit early, do not open modal
    }

    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);

    setCurrentTender({
      ...tender,
      file: fileUrl,
    });

    setIsViewModalOpen(true);
  } catch (error) {
    console.error("Error loading file:", error);
    toast.error("An unexpected error occurred while opening the document.");
  }
};


   


    const handleEdit = (id) => {
        const tender = tenders.find((item) => item.id === id);
        setCurrentTender(tender);
        setEditTitle(tender.title);
        setCurrentEditId(id);
        setIsEditModalOpen(true);

        if (fileInputRef.current) fileInputRef.current.value = "";
    };


    const handleDelete = async (id) => {
        const toastId = toast.warn(
            <div style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold" }}>
                <p>Are you sure you want to delete this tender?</p>
                <div className="d-flex justify-content-center">
                    <button
                        className="btn btn-danger btn-sm mx-2"
                        onClick={() => confirmDelete(id, toastId)}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => toast.dismiss(toastId)}
                    >
                        No
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeOnClick: false,
                toastClassName: "toast-container",
                closeButton: false,
            }
        );
    };

    // Confirms and executes the delete operation
    const confirmDelete = async (id, toastId) => {
        toast.dismiss(toastId); // Close confirmation toast
        const loadingToast = toast.loading("Deleting tender...");
        setLoading(true);
        try {
            const token = sessionStorage.getItem("userToken");
            const res = await AxiosInstance.post(
                `${BaseUrl}/api/notice/delete`, // Adjusted API endpoint for tenders
                { key: id }, // Assuming backend expects 'key' for ID
                {
                    headers: { "Content-Type": "application/json", Authorization: token },
                }
            );

            toast.dismiss(loadingToast);
            if (res.data.status === 200) {
                toast.success(res.data.message);
                // Re-fetch data for the current page to reflect changes
                fetchTenders(currentPage, searchQuery);
            } else {
                toast.error(res.data.message || "Failed to delete tender.");
            }
        } catch (error) {
            toast.dismiss(loadingToast);
            toast.error(error.response?.data?.message || "Error deleting tender.");
        } finally {
            setLoading(false);
        }
    };


    const handleTitleChange = (e) => {
        setNewTender((prevState) => ({
            ...prevState,
            title: e.target.value,
        }));
    };


    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]; // Added .doc and .docx

            if (!validTypes.includes(file.type)) {
                toast.error("Invalid file type. Please upload a PDF.");
                if (fileInputRef.current) fileInputRef.current.value = "";
                setNewTender((prevState) => ({ ...prevState, file: null }));
                return;
            }
            setNewTender((prevState) => ({ ...prevState, file }));
        } else {
            setNewTender((prevState) => ({ ...prevState, file: null }));
        }
    };



    const handleSave = async () => {
        if (!newTender.title || !newTender.file) {
            toast.error("Please fill in all required fields (Title and File).");
            return;
        }

        const token = sessionStorage.getItem("userToken");
        const formData = new FormData();
        formData.append("multipart", newTender.file);
        formData.append("title", newTender.title);

        setLoading(true);
        try {
            const res = await AxiosInstance.post(
                `${BaseUrl}/api/notice/create`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: token,
                    },
                }
            );

            if (res.data.status === 200) {
                toast.success(res.data.message);
                fetchTenders(1, searchQuery);
            } else {
                toast.error(res.data.message || "Failed to create tender.");
            }


            setNewTender({ title: "", file: null });
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (error) {
             handleAxiosError(error);

            setNewTender({ title: "", file: null });
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } finally {
            setLoading(false);
        }
    };


    const handleEditSave = async () => {
        if (editTitle.trim() === "") {
            toast.error("Title cannot be empty.");
            return;
        }

        const token = sessionStorage.getItem("userToken");
        const formData = new FormData();
        formData.append("title", editTitle);
        formData.append("key", currentEditId);


        if (fileInputRef.current && fileInputRef.current.files[0]) {
            const file = fileInputRef.current.files[0];
            const validTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
            const maxFileSize = 5 * 1024 * 1024;

            if (!validTypes.includes(file.type)) {
                toast.error("Invalid file type. Please upload a PDF, DOC, or DOCX.");
                fileInputRef.current.value = "";
                return;
            }
            formData.append("multipart", file);
        }

        setLoading(true);
        try {
            const res = await AxiosInstance.post(
                `${BaseUrl}/api/notice/update`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: token,
                    },
                }
            );

            if (res.data.status === 200) {
                toast.success(res.data.message);
                fetchTenders(currentPage, searchQuery);
                setIsEditModalOpen(false);
            } else {
                toast.error(res.data.message || "Failed to update tender.");
            }
        } catch (error) {
            handleAxiosError(error);
        } finally {
            setLoading(false);
        }
    };


    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <div className="home-dashboard">
                    <div className="ticket-tab">
                        <h3>Upload New Tender</h3>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    Title<span style={{ color: "red", fontSize: "1rem" }}>*</span>
                                </label>
                                <input
                                    className="form-control"
                                    name="title"
                                    value={newTender.title}
                                    onChange={handleTitleChange}
                                    placeholder="Enter Title for the Tender"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">
                                    Upload File <span style={{ color: "red", fontSize: "1rem" }}>*</span>
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    name="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                />
                            </div>
                            <div className="col-md-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-success mt-3"
                                    onClick={handleSave}
                                >
                                    Save Tender
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="ticket-tab">
                        <div className="row mb-4">
                            <div className="col-6">
                                <h3>Manage Tenders</h3>
                            </div>

                            <div className="col-6">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by title..."
                                    value={searchQuery}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th style={{ width: "15%" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenders.length > 0 ? (
                                        tenders.map((tender) => (
                                            <tr key={tender.id}>
                                                <td>{tender.title}</td>
                                                <td>
                                                    <FaEye
                                                        className="me-2"
                                                        style={{ cursor: "pointer", color: "blue" }}
                                                        onClick={() => handleView(tender.id)}
                                                    />
                                                    <FaPencil
                                                        className="me-2"
                                                        style={{ cursor: "pointer", color: "orange" }}
                                                        onClick={() => handleEdit(tender.id)}
                                                    />
                                                    <FaTrash
                                                        style={{ cursor: "pointer", color: "red" }}
                                                        onClick={() => handleDelete(tender.id)}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={2} className="text-center">
                                                No Tenders Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination controls */}
                        <div className="row mt-4">
                            <div className="col-5">
                                Total : {tenders.length} of {totalItems}
                            </div>
                            <div className="col-7 d-flex justify-content-end align-items-center">
                                {/* Previous Button */}
                                <button
                                    style={{ width: "fit-content", borderRadius: "50%", padding: "8px 12px" }}
                                    className="btn btn-success me-2"
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                >
                                    <FaAngleLeft />
                                </button>

                                {/* Page Numbers */}
                                <div className="d-flex">
                                    {Array.from({ length: totalPages }).map((_, index) => {
                                        const pageNum = index + 1;
                                        // Logic to show a limited number of page buttons around the current page
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`btn mx-1 ${currentPage === pageNum ? "active btn-primary" : "btn-light"}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            (pageNum === currentPage - 2 && currentPage - 2 > 1) ||
                                            (pageNum === currentPage + 2 && currentPage + 2 < totalPages)
                                        ) {
                                            return <span key={`ellipsis-${pageNum}`} className="mx-1">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                {/* Next Button */}
                                <button
                                    style={{ width: "fit-content", borderRadius: "50%", padding: "8px 12px" }}
                                    className="btn btn-success ms-2"
                                    onClick={handleNextPage}
                                    disabled={currentPage >= totalPages}
                                >
                                    <FaAngleRight />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* View Tender Modal */}
                    <Modal
                        isOpen={isViewModalOpen}
                        toggle={() => setIsViewModalOpen(false)}
                        centered
                        size="lg" // Larger modal for better PDF viewing
                    >
                        <ModalHeader toggle={() => setIsViewModalOpen(false)}>
                            View Tender
                        </ModalHeader>
                        <ModalBody>
                            {currentTender && (
                                <>
                                    <h5>{currentTender.title}</h5>
                                    {currentTender.file ? (
                                        <iframe
                                            src={currentTender.file}
                                            title="Tender Document"
                                            style={{ width: "100%", height: "500px", border: "1px solid #ccc" }}
                                            onError={(e) => console.error("Error loading PDF:", e)}
                                        >
                                            <p>Your browser does not support iframes. You can <a href={currentTender.file} target="_blank" rel="noopener noreferrer">download the file here</a>.</p>
                                        </iframe>
                                    ) : (
                                        <p>No document available for this tender.</p>
                                    )}
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => setIsViewModalOpen(false)}>
                                Close
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {/* Edit Tender Modal */}
                    <Modal
                        isOpen={isEditModalOpen}
                        toggle={() => setIsEditModalOpen(false)}
                        centered
                    >
                        <ModalHeader toggle={() => setIsEditModalOpen(false)}>
                            Edit Tender
                        </ModalHeader>
                        <ModalBody>
                            <div className="mb-3">
                                <label htmlFor="editTitle" className="form-label">
                                    Title<span style={{ color: "red", fontSize: "1rem" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="editTitle"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    placeholder="Enter tender title"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Upload New File (PDF, DOC, DOCX)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    ref={fileInputRef} // Use this ref for the file input in edit modal
                                    onChange={handleFileChange}
                                />
                                {currentTender?.file && (
                                    <small className="form-text text-muted mt-1">
                                        Current file: <a href={currentTender.file} target="_blank" rel="noopener noreferrer">View Current</a>
                                        <br />
                                        Upload new file to replace existing.
                                    </small>
                                )}
                            </div>
                        </ModalBody>
                        <ModalFooter className="flex-nowrap">
                            <Button color="success" onClick={handleEditSave}>
                                Save Changes
                            </Button>
                            <Button
                                color="secondary"
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                Cancel
                            </Button>
                        </ModalFooter>
                    </Modal>
                </div>
            )}
        </>
    );
};

export default Tender;